import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'

import { TGetItemFn } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { prepareContactStateRoutesMap } from '@/utils/paymentScreenUtils'
import { getPath } from '@/utils/urlUtils'

import { Spinner } from '@/components/spinner'

import { ApiService } from '@/api'
import {
  TContactAccessState,
  TCreatePayPalSubscriptionPayload,
  TSubscribePayPalV2Payload,
} from '@/api/types'
import { emailKey, tokenHeader } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { contactNameKey } from '@/core/constants'
import { ISessionContext } from '@/core/session'
import { useSession } from '@/core/session/useSession'
import { trackPaymentInfoAdded, trackPaymentClick, trackPurchaseStarted } from '@/events/events'

import { TPayPalButton } from './types'

function preparePayPalPayload(
  getItem: TGetItemFn,
  sessionContext: ISessionContext,
  intent_id: string
): TSubscribePayPalV2Payload {
  const contactName = getItem(contactNameKey)
  const sessionId = sessionContext.sessionId
  const utmParams = sessionContext.utmParams
  const fbParams = sessionContext.fbParams || {}
  const gaParams = sessionContext.gaParams || {}
  const firstVisitAt = sessionContext.fva
  const firstVisitAtDiff: number = +new Date() - +new Date(firstVisitAt)

  const email = getItem('testEmail')
  if (email) {
    console.log(`testEmail '${email} detected`)
  }

  return {
    session_id: sessionId,
    test_email: email,
    utm_params: {
      ...utmParams,
      ...fbParams,
      ...gaParams,
    },
    first_visit_at_diff: firstVisitAtDiff,
    name: contactName,
    intent_id,
  }
}

export const PayPalButtonV2 = ({
  pageName,
  paypalButtonConfigStyle,
  productData,
  setItem,
  getItem,
  config,
  customTagline,
  successRedirectTo,
  contactStateRoutesMap,
}: TPayPalButton) => {
  const { authUser, logout } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()
  const [{ isPending }] = usePayPalScriptReducer()
  const [isPaypalInitialCheckoutEventIsSent, setPaypalInitialCheckoutEventIsSent] = useState(false)
  const { currency, price } = productData
  const semaphore = useRef<boolean>(false)
  const subscriptionIntentId = useRef<string | undefined>(undefined)

  const createSubscription = useCallback(async () => {
    if (semaphore.current) {
      return Promise.reject()
    }

    const payload: TCreatePayPalSubscriptionPayload = {
      session_id: sessionContext.sessionId,
      page_set_id: config.PAGE_SET_ID,
      plan_id: productData.internalPlanId,
    }
    semaphore.current = true
    try {
      const response = await ApiService.createPayPalSubscription(payload)

      semaphore.current = false

      subscriptionIntentId.current = response.data.intent_id
      return response.data.subscription_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.log('createSubscription err', e)
      subscriptionIntentId.current = undefined
      semaphore.current = false

      navigate(getPath(PageName.paymentFailed))
      return Promise.reject(e)
    }
  }, [config, productData, navigate, subscriptionIntentId, sessionContext])

  const onApprove = useCallback(async () => {
    console.log('subscriptionIntentId', subscriptionIntentId.current)

    if (!subscriptionIntentId.current) {
      navigate(getPath(PageName.paymentFailed))
      return
    }
    trackPurchaseStarted(
      pageName,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      productData.internalPlanId,
      PaymentMethod.PAYPAL,
      PaymentPlatform.PAYPAL,
      sessionContext
    )
    setItem(PageName.paymentScreen, 'paypal')

    const payload = preparePayPalPayload(getItem, sessionContext, subscriptionIntentId.current)

    try {
      resetContactStateCheckTimeout(setItem)
      const response = await ApiService.subscribePayPalPlanV2(payload)
      console.log('subscribe', response)

      authUser(response.data.contact_id, response.headers[tokenHeader], response.data.email)
      navigate(getPath(successRedirectTo))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      semaphore.current = false
      console.log('subscribe error', e)

      await logout()

      setItem(emailKey, e.response?.data?.email ? e.response.data.email : '')

      if (e.response?.status === 409) {
        const state = e.response?.data?.state
        if (state) {
          const routesMap = prepareContactStateRoutesMap(contactStateRoutesMap || {})
          const path = routesMap[state as TContactAccessState]
          if (path) {
            navigate(path)
            return
          }
        }
      }
      navigate(getPath(PageName.paymentFailed))
    }
  }, [
    navigate,
    config,
    productData,
    sessionContext,
    subscriptionIntentId,
    authUser,
    getItem,
    logout,
    pageName,
    setItem,
    successRedirectTo,
    contactStateRoutesMap,
  ])

  const handlePaypalButtonCLick = async () => {
    if (!isPaypalInitialCheckoutEventIsSent) {
      setItem('paymentMethod', PaymentMethod.PAYPAL)
      setItem('paymentPlatform', PaymentPlatform.PAYPAL)
      trackPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        productData.payPalPlanId,
        PaymentMethod.PAYPAL,
        PaymentPlatform.PAYPAL,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        null,
        [],
        ['paypal']
      )
      trackPaymentClick(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        productData.payPalPlanId,
        PaymentMethod.PAYPAL,
        PaymentPlatform.PAYPAL,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID
      )
      setPaypalInitialCheckoutEventIsSent(true)
    }
  }

  return (
    <>
      {customTagline && <div className="paypal-button-custom-tagline">{customTagline}</div>}
      {isPending && <Spinner />}
      <PayPalButtons
        style={{ ...paypalButtonConfigStyle.style }}
        disabled={false}
        forceReRender={[price, currency]}
        fundingSource={undefined}
        onClick={handlePaypalButtonCLick}
        onError={() => {}}
        createSubscription={createSubscription}
        onApprove={onApprove}
      />
    </>
  )
}
